import { createContext, useContext, type ReactNode } from "react";
import type { City } from "@/lib/city";

interface CityContextValue {
  city: City;
  /**
   * Helper for building paths scoped to the current city.
   * @example link(`/directory`) === '/canberra/directory'
   */
  link: (subpath: string) => string;
}

const CityContext = createContext<CityContextValue | null>(null);

export function CityProvider({
  city,
  children,
}: {
  city: City;
  children: ReactNode;
}) {
  const value: CityContextValue = {
    city,
    link: (subpath: string) => {
      const clean = subpath.startsWith("/") ? subpath : `/${subpath}`;
      return clean === "/" ? "/" : clean;
    },
  };

  return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
}

export function useCity(): CityContextValue {
  const ctx = useContext(CityContext);
  if (!ctx) {
    throw new Error(
      "useCity must be used within a CityProvider (nested under a city-scoped route)",
    );
  }
  return ctx;
}
